import React, { ChangeEvent, useRef, useState } from 'react';

import { useMutation } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';

import { EligibilityEmailFlagField, bulkMarkEligibilityEmailFlag } from '~/services/LuxLoyalty';

import { getBulkMarkEmailFlagResultMessage, parseEmailsCsvText } from './infiniteEligibilityBulkMarkEmail';
import { EMAIL_FLAG_FIELD_LABELS } from './infiniteEligibilityBulkUpdate';

const FIELD_OPTIONS: Array<EligibilityEmailFlagField> = ['registerInterestEmailSent', 'conciergeIntroEmailSent'];

export default function BulkMarkEmailFlagDialog({
  open,
  onClose,
  onCompleted,
}: {
  open: boolean;
  onClose: () => void;
  onCompleted?: () => void;
}) {
  const { enqueueSnackbar } = useSnackbar();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [field, setField] = useState<EligibilityEmailFlagField>('registerInterestEmailSent');
  const [emails, setEmails] = useState<Array<string>>([]);
  const [csvFileName, setCsvFileName] = useState<string | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => bulkMarkEligibilityEmailFlag(field, emails),
    onSuccess: (response) => {
      const hasIssues = response.result.notFoundEmails.length > 0 || response.result.skippedIneligibleEmails.length > 0;
      enqueueSnackbar(getBulkMarkEmailFlagResultMessage(response.result), {
        variant: hasIssues ? 'warning' : 'success',
      });
      onCompleted?.();
      handleClose();
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message, { variant: 'error' });
    },
  });

  const handleClose = () => {
    setField('registerInterestEmailSent');
    setEmails([]);
    setCsvFileName(null);
    setParseError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onClose();
  };

  const handleCsvFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    setParseError(null);
    setEmails([]);
    setCsvFileName(file?.name ?? null);

    if (!file) {
      return;
    }

    const { emails: parsedEmails, invalidRows } = parseEmailsCsvText(await file.text());

    if (parsedEmails.length === 0) {
      setParseError('CSV file has no valid email addresses.');
      return;
    }

    if (invalidRows.length > 0) {
      setParseError(invalidRows.join('\n'));
    }

    setEmails(parsedEmails);
  };

  const openCsvFilePicker = () => {
    if (!fileInputRef.current) {
      return;
    }

    fileInputRef.current.value = '';
    fileInputRef.current.click();
  };

  return (
    <Dialog open={open} onClose={mutation.isPending ? undefined : handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Bulk mark email flags</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Alert severity="info">
            Upload a CSV with an <strong>Email</strong> column to bulk mark an email flag as sent for existing eligible
            users.
          </Alert>
          <FormControl>
            <RadioGroup value={field} onChange={(event) => setField(event.target.value as EligibilityEmailFlagField)}>
              {FIELD_OPTIONS.map((option) => (
                <FormControlLabel
                  key={option}
                  value={option}
                  control={<Radio disabled={mutation.isPending} />}
                  label={EMAIL_FLAG_FIELD_LABELS[option]}
                />
              ))}
            </RadioGroup>
          </FormControl>
          <Box>
            <input ref={fileInputRef} type="file" accept=".csv,text/csv" hidden onChange={handleCsvFileChange} />
            <Button
              variant="outlined"
              startIcon={<CloudUploadIcon />}
              onClick={openCsvFilePicker}
              disabled={mutation.isPending}
            >
              Choose CSV
            </Button>
            {csvFileName && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Selected: {csvFileName}
              </Typography>
            )}
          </Box>
          {parseError && (
            <Alert severity="warning" sx={{ whiteSpace: 'pre-wrap' }}>
              {parseError}
            </Alert>
          )}
          {emails.length > 0 && (
            <Typography variant="body2" color="text.secondary">
              {emails.length} email{emails.length === 1 ? '' : 's'} ready to be marked.
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={mutation.isPending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => mutation.mutate()}
          disabled={emails.length === 0 || mutation.isPending}
        >
          Mark {emails.length || ''} email{emails.length === 1 ? '' : 's'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
